import apiService, { ServiceType } from './api';
import type { ImportJobStatus, ImportStarted } from './importJobApi';
import { 
  FutureInstrument, 
  StockInstrument, 
  PairTradingConfig,
  PositionCalculation 
} from '../types';

interface SearchResult<T> {
  results: T[];
  total: number;
}

interface PriceMap {
  [symbol: string]: number;
}

interface CapitalAllocationRequest {
  futureSymbol: string;
  stockSymbols: string[];
  totalCapital: number;
  basis: number;
  takeProfit: number;
  stopLoss: number;
}

/** Capacity probe + split preview for the pair-level deposit/withdraw modal. */
export interface PairCashCapacity {
  maxDeposit: string;
  maxWithdraw: string;
  stockIdle: string;
  derivIdle: string;
  /** Σ currentCapital per leg — funded capital, independent of holdings. Gates Start. */
  stockAllocated: string;
  derivAllocated: string;
  marginRate: number;
  splitFraction: { stock: number; deriv: number };
  /** ANY strategy (grid or pair) active on the portfolio — gates deposit/withdraw. */
  botActive: boolean;
  /** PAIR execution active — drives the Start button ("Pair is already running"). */
  pairBotActive: boolean;
  warnings: string[];
}

export interface PairActiveResponse {
  dataConfigId: string | null;
}

/** DRAFT (never opened) → ACTIVE (opened ≥1 pair) → ENDING (all closed, settling) → ENDED (history). */
export type PairLifecycleStatus = 'DRAFT' | 'ACTIVE' | 'ENDING' | 'ENDED';

/** One pair config on a stock portfolio (GET /pair/list/:portfolioId). */
export interface PairListItem {
  dataConfigId: string;
  name: string | null;
  lifecycleStatus: PairLifecycleStatus;
  /** The one pair live on the account (funded, opened or running). */
  live: boolean;
  running: boolean;
  /** "EXCH:SYM" */
  stockSymbols: string[];
  futureSymbol: string | null;
  /** Raw VND. */
  totalCapital: string;
  /** Raw VND. */
  realizedPnl: string;
  firstOpenedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PairListResponse {
  pairs: PairListItem[];
  /** What portfolio-addressed endpoints resolve to: the live pair, else the newest draft. */
  currentDataConfigId: string | null;
}

/** Final settlement of an ENDED pair. Money fields are raw VND strings. */
export interface PairEndSummary {
  endReason: string | null;
  firstOpenedAt: string | null;
  settled: string;
  realizedPnl: string;
  legs: {
    symbol: string;
    leg: 'STOCK' | 'DERIV';
    deposited: string;
    withdrawnBefore: string;
    settled: string;
    realizedPnl: string;
  }[];
  closedPairs: { date: string; long: string[]; short: string[] }[];
}

export interface PairDetail {
  dataConfigId: string;
  name: string | null;
  portfolioId: string;
  lifecycleStatus: PairLifecycleStatus;
  firstOpenedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  configData: Record<string, unknown> | null;
  summary: PairEndSummary | null;
  legs: {
    instrumentConfigId: string;
    leg: 'STOCK' | 'DERIV';
    exchange: string;
    symbol: string;
    status: string;
    settings: Record<string, unknown> | null;
    currentCapital: string;
    realizedPnl: string;
    heldQty: string;
  }[];
}

/** One open position (per instrument, both legs) for the Position tab under the chart. */
export interface PairAccountingLeg {
  instrument: string;
  side: 'LONG' | 'SHORT';
  quantity: number;
  // Per-unit cost in the same display unit as currentPrice
  // (stock: VND/share; futures: index points/contract).
  costPrice: number;
  /** Null when no price is known yet — and then the PnL is unknown too. */
  currentPrice: number | null;
  pnl: number | null;
}

export interface PairAccountingPair {
  pairId: string;
  legs: PairAccountingLeg[];
  /** Cash the pair banked: the futures leg the expiry roll closed (raw VND). */
  realizedPnl?: number;
  /** Open legs + realizedPnl, as the bot's TP/CL counts it. Null when a leg has no price. */
  totalPnl: number | null;
}

/** An import job on this account's pair that is not READY / FAILED / CANCELLED yet. */
export interface PairPendingImport {
  id: string;
  kind: string;
  status: ImportJobStatus;
  error: string | null;
}

export interface PairAccountingResponse {
  success: boolean;
  pairs: PairAccountingPair[];
  /** While set, the pairs are the engine's book from before the import. */
  pendingImport?: PairPendingImport | null;
}

/** One stock leg of the engine's OPEN-trigger breakdown. */
export interface PairReferencePnlStockLeg {
  instrument: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  proportion: number;
  currencyUnit: number;
  /** Operator's reference price, thousand VND. */
  reference: number | null;
  /** Live price the ENGINE marked against, thousand VND. */
  market: number | null;
  /** Shares in ONE pair's basket. Null when the engine never got to sizing. */
  quantity: number | null;
  /** Raw VND. */
  pnl: number | null;
  /** Why a leg reads 0 (e.g. its slice rounds below one board lot). */
  note: string | null;
}

/**
 * The v3 OPEN trigger exactly as the pair engine evaluated it, per leg
 * (GET /pair/reference-pnl/:portfolio).
 *
 * PER PAIR = ONE contract — what the bot places per trigger. NOT scaled by
 * allocated cash, so it must always be labelled per-pair on screen.
 *
 * The UI does not recompute any of this: the engine publishes it on every
 * futures tick and this is a read. `reporting: false` means no engine has
 * reported recently (a stopped bot) — fall back to the local preview in
 * utils/pairReferencePnl.ts and say so.
 */
export interface PairReferencePnl {
  reporting: boolean;
  at: string | null;
  ageSeconds: number | null;
  scope: 'per_pair';
  contracts: number;
  activeSide: 'BUY' | 'SELL';
  isPause: boolean;
  maxOpen: number;
  openContracts: number;
  future: {
    instrument: string | null;
    side: 'LONG' | 'SHORT';
    quantity: number;
    multiplier: number;
    /** Index points. */
    reference: number | null;
    market: number | null;
    /** Raw VND. */
    pnl: number | null;
  } | null;
  /** Contract the reference was entered against; differs after a failed roll. */
  referenceSymbol: string | null;
  stocks: PairReferencePnlStockLeg[];
  /** Raw VND. Null ⇒ trigger undefined, see `undefinedReason`. */
  total: number | null;
  undefinedReason: string | null;
  /** Why a well-defined negative PnL still opened nothing. */
  blockedReason: string | null;
}

/**
 * One order as the pair BOT sees it, read from the bot's own Redis journal —
 * not from the broker/accounting API. That distinction is the point: the journal
 * carries CREATED / SENT_TO_BROKER rows the broker has not acknowledged, which
 * is where a stuck bot shows up.
 */
export interface PairBotOrder {
  clientOrderId: string;
  serverOrderId: string | null;
  leg: 'STOCK' | 'FUTURES';
  instrument: string;
  symbol: string;
  side: string;
  orderType: string;
  status: string;
  cancelStatus: string | null;
  quantity: number;
  matchedQuantity: number;
  /** Native unit: thousand-VND for stocks, index points for futures. */
  limitPrice: number | null;
  avgPrice: number | null;
  lastModified: number | null;
  isFinal: boolean;
  /** Seconds since the last recorded state change; -1 = placed, never acked. */
  ageSeconds: number;
  strategyId: string | null;
}

export interface PairBotOrdersResponse {
  success: boolean;
  pid: string;
  date: string;
  liveCount: number;
  stalledCount: number;
  orders: PairBotOrder[];
}


// ---------------------------------------------------------------------------
// Futures-roll plan (GET/PUT /pair/:dataConfigId/roll-plan)
// ---------------------------------------------------------------------------

export type RollMode = 'AUTO' | 'MANUAL' | 'OFF';

export interface ResolvedRollPlan {
    mode: RollMode;
    contract: string;
    /** ISO date; null when the contract id carries no readable YYMM. */
    expiry: string | null;
    /** ISO date the roll fires; null for OFF. */
    rollDate: string | null;
    rollTime: string;
    source: 'user' | 'auto' | 'off' | 'unknown-contract';
    note?: string;
    nextContract: string | null;
}

export interface RollWarningState {
    warn: boolean;
    sessionsToExpiry: number | null;
    acknowledged: boolean;
    signature: string;
    severity: 'none' | 'info' | 'warning' | 'critical';
    message?: string;
}

export interface OrphanStatus {
    orphaned: boolean;
    reason?: string;
    contract?: string;
    expiry?: string | null;
    openPairs: number;
    derivHeldQty: string;
    stockLegsHeld: { symbol: string; exchange: string; heldQty: string; holdingsCost: string }[];
    engineAlert?: Record<string, unknown> | null;
}

export interface RollPlanStatus {
    dataConfigId: string;
    derivInstrumentConfigId: string | null;
    derivSymbol: string | null;
    isRunning: boolean;
    plan: ResolvedRollPlan;
    defaultRollDate: string | null;
    warning: RollWarningState;
    /** The roll date passed with the contract still un-rolled (bot was stopped). */
    missed: boolean;
    referencePrice: number | null;
    referenceContract: string | null;
    engine: Record<string, unknown> | null;
    orphan: OrphanStatus;
    lastAlert: Record<string, unknown> | null;
    /** Server's app-local today, so the UI never computes VN dates itself. */
    today: string;
}

export interface PairImportStockLeg {
    symbol: string;
    exchange?: string;
    weight: number;
    /** Total shares to import, split evenly over `pairs` in lots of 100; cost is the broker's. */
    qty: number;
    reference_price?: number;
}

export interface PairImportPreview {
    dataConfigId: string;
    current: {
        createdAt: string;
        derivSymbol: string | null;
        derivExpiry: string | null;
        derivExpired: boolean;
        openBookPairs: number;
        legs: {
            instrumentConfigId: string;
            leg: 'STOCK' | 'DERIV';
            exchange: string;
            symbol: string;
            status: string;
            currentCapital: string;
            holdingsCost: string;
            heldQty: string;
        }[];
        totalCommitted: string;
    };
    broker: {
        stock: BrokerLegSnapshot | null;
        deriv: BrokerLegSnapshot | null;
    };
    proposal: {
        stock_legs: PairImportStockLeg[];
        deriv_symbol: string;
        /** k: trading pairs, one futures contract each. */
        pairs: number;
        /** Contracts no other bot claims: the ceiling for `pairs`. */
        free_futures: number;
        stock_allocated_cash: number;
        deriv_allocated_cash: number;
        future_instrument_price: number | null;
    };
    isRunning: boolean;
    warnings: string[];
    confirmationPhrase: string;
}

export interface BrokerLegSnapshot {
    portfolioId: string;
    externalPortfolioId: string;
    accountType: string;
    cash: number | null;
    available: number | null;
    releasedByReset: number;
    allocatableAfterReset: number | null;
    error?: string;
    stockHoldings: { exchange: string; symbol: string; heldQty: number; holdingsCost: number }[];
    futuresHoldings: { exchange: string; symbol: string; heldQty: number; expired: boolean }[];
}

export interface PairImportRequest {
    confirm: string;
    stock_legs: PairImportStockLeg[];
    /** k: 0 rebuilds flat (every leg qty 0). */
    pairs: number;
    deriv_symbol?: string;
    stock_allocated_cash: number;
    deriv_allocated_cash: number;
    future_instrument_price?: number;
}

export const pairTradingApi = {
  /**
   * Search for futures instruments
   */
  async searchFutures(query: string): Promise<SearchResult<FutureInstrument>> {
    try {
      const response = await apiService.getService(ServiceType.CORE).get('/search/futures', {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching futures:', error);
      throw error;
    }
  },

  /**
   * Search for stock instruments
   */
  async searchStocks(query: string): Promise<SearchResult<StockInstrument>> {
    try {
      const response = await apiService.getService(ServiceType.CORE).get('/search/stocks', {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching stocks:', error);
      throw error;
    }
  },

  /**
   * Get detailed information about a futures contract
   */
  async getFutureDetails(symbol: string): Promise<FutureInstrument> {
    try {
      const response = await apiService.getService(ServiceType.CORE).get(`/api/futures/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching future details:', error);
      throw error;
    }
  },

  /**
   * Get detailed information about a stock
   */
  async getStockDetails(symbol: string): Promise<StockInstrument> {
    try {
      const response = await apiService.getService(ServiceType.CORE).get(`/api/stocks/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock details:', error);
      throw error;
    }
  },

  /**
   * Calculate basis between futures and spot price
   */
  calculateBasis(futurePrice: number, spotPrice: number): number {
    return futurePrice - spotPrice;
  },

  /**
   * Get capital allocation from backend
   */
  async getCapitalAllocation(request: CapitalAllocationRequest): Promise<PairTradingConfig['capitalAllocation']> {
    try {
      const response = await apiService.getService(ServiceType.CORE).post('/api/pair-trading/allocation', request);
      return response.data;
    } catch (error) {
      console.error('Error fetching capital allocation:', error);
      throw error;
    }
  },

  /**
   * Calculate positions based on capital and prices
   */
  calculatePositions(
    capital: number,
    prices: PriceMap,
    allocation: PairTradingConfig['capitalAllocation'],
    futureSymbol: string,
    stockSymbols: string[]
  ): PositionCalculation[] {
    const positions: PositionCalculation[] = [];

    // Calculate futures position
    if (prices[futureSymbol]) {
      const futureCapital = allocation.futures;
      const futurePrice = prices[futureSymbol];
      const futureQuantity = futureCapital / futurePrice;

      positions.push({
        instrument: futureSymbol,
        side: 'long',
        quantity: futureQuantity,
        entryPrice: futurePrice,
        value: futureCapital,
        weight: (futureCapital / allocation.total) * 100
      });
    }

    // Calculate stock positions
    const stockCapital = allocation.stocks;
    const perStockCapital = stockCapital / stockSymbols.length;

    stockSymbols.forEach(symbol => {
      if (prices[symbol]) {
        const stockPrice = prices[symbol];
        const stockQuantity = perStockCapital / stockPrice;

        positions.push({
          instrument: symbol,
          side: 'short',
          quantity: stockQuantity,
          entryPrice: stockPrice,
          value: perStockCapital,
          weight: (perStockCapital / allocation.total) * 100
        });
      }
    });

    return positions;
  },

  /**
   * Calculate P&L at take profit level
   */
  calculateTakeProfitPnL(
    positions: PositionCalculation[],
    takeProfit: number
  ): number {
    return positions.reduce((total, position) => {
      if (position.side === 'long') {
        return total + (position.quantity * takeProfit);
      } else {
        return total - (position.quantity * takeProfit);
      }
    }, 0);
  },

  /**
   * Calculate P&L at stop loss level
   */
  calculateStopLossPnL(
    positions: PositionCalculation[],
    stopLoss: number
  ): number {
    return positions.reduce((total, position) => {
      if (position.side === 'long') {
        return total - (position.quantity * stopLoss);
      } else {
        return total + (position.quantity * stopLoss);
      }
    }, 0);
  },

  /**
   * Calculate risk/reward ratio
   */
  calculateRiskReward(takeProfitPnL: number, stopLossPnL: number): number {
    if (stopLossPnL === 0) return 0;
    return Math.abs(takeProfitPnL / stopLossPnL);
  },

  /**
   * Calculate portfolio delta (net exposure)
   */
  calculatePortfolioDelta(positions: PositionCalculation[]): number {
    return positions.reduce((delta, position) => {
      const positionValue = position.quantity * position.entryPrice;
      return position.side === 'long' ? delta + positionValue : delta - positionValue;
    }, 0);
  }
  ,
  // ---------------- Pair-level cash ops (one amount → both legs atomically) ----------------

  /** Resolve the active pair DataConfig.id for a portfolio. Returns null when no pair is placed. */
  async getActivePair(portfolioId: string): Promise<PairActiveResponse> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairActiveResponse>(`/pair/active/${portfolioId}`);
    return response.data;
  },

  /** Every pair config on a stock portfolio: drafts, the live one and history. */
  async listPairs(portfolioId: string): Promise<PairListResponse> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairListResponse>(`/pair/list/${portfolioId}`);
    return response.data;
  },

  /** One pair: config, legs, lifecycle and (once ENDED) its settlement summary. */
  async getPairDetail(dataConfigId: string): Promise<PairDetail> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairDetail>(`/pair/${dataConfigId}/detail`);
    return response.data;
  },

  /** Display name only — allowed in any status, history included. */
  async renamePair(dataConfigId: string, name: string): Promise<unknown> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .patch(`/pair/${dataConfigId}/name`, { name });
    return response.data;
  },

  /** End now: a DRAFT is discarded, an ACTIVE pair must be stopped and flat (409 otherwise). */
  async endPair(dataConfigId: string): Promise<{ status: string; withdrawn?: unknown }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<{ status: string; withdrawn?: unknown }>(`/pair/${dataConfigId}/end`);
    return response.data;
  },

  /** Capacity probe used by the modal — clamps the input + previews leg split. */
  async pairCashCapacity(dataConfigId: string): Promise<PairCashCapacity> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairCashCapacity>(`/pair/${dataConfigId}/cash-capacity`);
    return response.data;
  },

  /** `idempotencyKey`: one per user action, so a retry reads back instead of re-running. */
  async depositToPair(dataConfigId: string, amount: number, idempotencyKey?: string): Promise<unknown> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post(`/pair/${dataConfigId}/deposit`, {
        amount,
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      });
    return response.data;
  },

  /** `idempotencyKey`: one per user action, so a retry reads back instead of re-running. */
  async withdrawFromPair(dataConfigId: string, amount: number, idempotencyKey?: string): Promise<unknown> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post(`/pair/${dataConfigId}/withdraw`, {
        amount,
        ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
      });
    return response.data;
  },

  /**
   * Start a previously-saved pair (Option B). placePair with cash=0 just
   * persists the DataConfig+IC rows; the user funds each leg via deposit, then
   * calls this to publish the start payload and create the StrategyExecution.
   */
  async startPair(dataConfigId: string): Promise<{
    started: boolean;
    alreadyRunning?: boolean;
    executionId?: string;
    pid?: string;
    dataConfigId: string;
    message?: string;
  }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post(`/pair/${dataConfigId}/start`);
    return response.data;
  },

  /** Stop today's StrategyExecution for the pair. Required before deposit/withdraw or close. */
  async stopPair(dataConfigId: string): Promise<{ status: string }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<{ status: string }>(`/pair/${dataConfigId}/stop`);
    return response.data;
  },

  /**
   * Close every open pair at market NOW and leave the bot running.
   *
   * Not a stop — the worker stays up, idle, because `is_pause` is set as part of
   * this call. Clearing the Pause switch resumes it.
   *
   * Asynchronous: closing needs real fills, so this only queues the command.
   * Poll `getFlattenResult(dataConfigId, requestId)` for the outcome.
   *
   * Refused (409) when the bot isn't running or the market is shut — including the
   * 11:30–13:00 lunch break, since closing a position needs a matching session.
   */
  async flattenPair(dataConfigId: string): Promise<{ status: 'REQUESTED'; requestId: string }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<{ status: 'REQUESTED'; requestId: string }>(`/pair/${dataConfigId}/flatten`);
    return response.data;
  },

  /**
   * Result of a queued flatten. `status: 'PENDING'` while the worker is still
   * closing; `'DONE'` once it reported, with `reason` naming why anything is left
   * (`partial`, `market_closed`, `action_lock_timeout`, `bot_stopped`).
   */
  async getFlattenResult(
    dataConfigId: string,
    requestId: string,
  ): Promise<{
    status: 'PENDING' | 'DONE';
    attempted?: number;
    closed?: number;
    remaining?: number;
    orders_cancelled?: number;
    reason?: string | null;
  } | null> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get(`/pair/${dataConfigId}/flatten/${requestId}`);
    return response.data;
  },

  /** Close (archive) the pair. Backend enforces: bot stopped, no positions, no remaining cash. */
  async closePair(dataConfigId: string): Promise<{ closed: number }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .delete<{ closed: number }>(`/pair/${dataConfigId}/close`);
    return response.data;
  },

  /** Per-instrument open positions + PnL (both legs) for the Position tab. */
  async getAccounting(portfolioId: string): Promise<PairAccountingResponse> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairAccountingResponse>(`/pair/accounting/${portfolioId}`);
    return response.data;
  },

  /**
   * The engine's own OPEN-trigger breakdown. Read-only pass-through of the
   * snapshot the pair worker publishes — never recompute it here.
   */
  async getReferencePnl(portfolioId: string): Promise<PairReferencePnl> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairReferencePnl>(`/pair/reference-pnl/${portfolioId}`);
    return response.data;
  },

  /** Today's orders straight from the pair bot's Redis journal. */
  async getBotOrders(dataConfigId: string): Promise<PairBotOrdersResponse> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairBotOrdersResponse>(`/pair/orders/${dataConfigId}`);
    return response.data;
  },

  /**
   * Operator-initiated roll of the futures leg (POST /pair/:dc/roll).
   * Both fields are required: the BE refuses to derive the reference price
   * because an expired contract no longer quotes a basis to shift by.
   */
  async rollDerivLeg(
    dataConfigId: string,
    newSymbol: string,
    newReferencePrice: number,
  ): Promise<{ updated: boolean; reason?: string; old?: string; new?: string }> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post(`/pair/${dataConfigId}/roll`, {
        new_symbol: newSymbol,
        new_reference_price: newReferencePrice,
      });
    return response.data;
  },

  /**
   * Futures-roll plan + expiry countdown + orphan diagnosis
   * (GET /pair/:dataConfigId/roll-plan).
   *
   * Every date in the response is resolved server-side against the holiday
   * calendar that is also published to the engine, so the UI renders dates
   * rather than deriving them — the third-Thursday rule is deliberately NOT
   * re-implemented here.
   */
  async getRollPlan(dataConfigId: string): Promise<RollPlanStatus> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<RollPlanStatus>(`/pair/${dataConfigId}/roll-plan`);
    return response.data;
  },

  /** Set the roll plan (PUT /pair/:dataConfigId/roll-plan). Works while running. */
  async setRollPlan(
    dataConfigId: string,
    body: {
      mode: RollMode;
      roll_date?: string;
      roll_time?: string;
      acknowledge_settlement?: boolean;
    },
  ): Promise<RollPlanStatus> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .put<RollPlanStatus>(`/pair/${dataConfigId}/roll-plan`, body);
    return response.data;
  },

  /**
   * Dismiss the expiry warning for the plan as shown (POST .../roll-plan/ack).
   * Sending the signature makes a stale dismissal fail instead of hiding a plan
   * the operator never saw.
   */
  async acknowledgeRollWarning(
    dataConfigId: string,
    signature?: string,
  ): Promise<RollPlanStatus> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<RollPlanStatus>(`/pair/${dataConfigId}/roll-plan/ack`, signature ? { signature } : {});
    return response.data;
  },

  /** Dry run of the rebuild-from-broker recovery (GET .../import/preview). */
  async previewPairImport(dataConfigId: string): Promise<PairImportPreview> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .get<PairImportPreview>(`/pair/${dataConfigId}/import/preview`);
    return response.data;
  },

  /** Archive the pair and re-import it from broker positions (destructive). Starts a job (202). */
  async applyPairImport(dataConfigId: string, body: PairImportRequest): Promise<ImportStarted> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<ImportStarted>(`/pair/${dataConfigId}/import`, body);
    return response.data;
  },

  /** End a pair stuck in ENDING without building a replacement (destructive). Starts a job (202). */
  async endPairFromBroker(dataConfigId: string, confirm: string): Promise<ImportStarted> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .post<ImportStarted>(`/pair/${dataConfigId}/end-from-broker`, { confirm });
    return response.data;
  },

  /** Update pair settings mid-flight (PATCH /pair/settings). */
  async updatePairSettings(
    portfolio: string,
    settings: Record<string, unknown>,
    dataConfigId?: string | null,
  ): Promise<unknown> {
    const response = await apiService
      .getService(ServiceType.CORE)
      .patch('/pair/settings', {
        portfolio,
        settings,
        ...(dataConfigId ? { data_config_id: dataConfigId } : {}),
      });
    return response.data;
  },
};

export default pairTradingApi;

